import { useEffect, useRef, useState } from "react";
import { logger } from "@/lib/logger";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { isNative, forceStatusBarDark } from "@/lib/capacitor";
import Splash from "@/pages/Splash";
import { checkResumeJobCrashLoop } from "@/lib/resumeJobGuard";
import { isBiometricOptedIn } from "@/lib/biometricAuth";
import { getRecoverableWorkflow, isAppraisalRoute } from "@/lib/activeWorkflowGuard";
import {
  decideStartupRoute,
  emitStartupEvent,
  getStoredAuthUserId,
  hasStoredAuthTokens,
  once,
} from "@/lib/startupRecovery";

const MIN_SPLASH_MS = 1000;
const ABSOLUTE_MAX_SPLASH_MS = 10000; // safety net if auth restoration never resolves
const FADE_OUT_MS = 220;

const BYPASS_PREFIXES = ["/reset-password", "/track"];

/**
 * Routes that are valid authenticated destinations.
 * If the app cold-starts on one of these, we should stay there
 * instead of forcibly redirecting to /dashboard.
 */
const PRESERVE_ROUTE_PREFIXES = [
  "/resume-job",
  "/job/",
  "/invoice/",
  "/appraisal/",
  "/create-job",
  "/create-driver-job",
  "/settings",
  "/jobs",
  "/pending-jobs",
  "/previous-jobs",
  "/customers",
  "/invoicing",
  "/analytics",
  "/schedule",
  "/driver-management",
  "/shortcuts",
  "/vehicle-daily-checks",
  "/quick-quote",
  "/help-guides",
  "/driver-settings",
  "/ready-to-invoice",
  "/my-messages",
  "/driver-messages",
  "/dashboard",
];

const shouldBypassRouting = (pathname: string) =>
  BYPASS_PREFIXES.some((p) => pathname.startsWith(p));

const shouldPreserveRoute = (pathname: string) =>
  PRESERVE_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));

/**
 * NativeStartupOverlay — 1.72 restart recovery.
 *
 * Exactly ONE startup routing decision is made (run-once `finish`). A valid
 * restored session is never re-authenticated with Face ID; a valid active
 * workflow is restored directly (cold start, WebView reload or relaunch)
 * without passing through /auth or /dashboard. The splash stays up until the
 * decision has been applied, so no intermediate route is visible.
 */
export function NativeStartupOverlay() {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, session } = useAuth();
  const startAtRef = useRef(Date.now());
  const [visible, setVisible] = useState<boolean>(isNative);
  const [fadingOut, setFadingOut] = useState(false);

  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const bootPathRef = useRef(location.pathname);
  const authRef = useRef({ status, session });
  authRef.current = { status, session };

  const unmountedRef = useRef(false);
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const schedule = (fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timersRef.current.delete(t);
      if (unmountedRef.current) return;
      fn();
    }, ms);
    timersRef.current.add(t);
  };

  // The single, idempotent startup completion.
  const finishRef = useRef(
    once((nextPath: string | null) => {
      if (unmountedRef.current) return;
      const remaining = Math.max(0, MIN_SPLASH_MS - (Date.now() - startAtRef.current));
      schedule(() => {
        setFadingOut(true);
        if (nextPath && pathnameRef.current !== nextPath) {
          navigate(nextPath, { replace: true });
        }
        schedule(() => {
          setVisible(false);
          forceStatusBarDark();
        }, FADE_OUT_MS);
      }, remaining);
    }),
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      unmountedRef.current = true;
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  /** Compute + apply the one startup decision. */
  const decide = (source: "auth_resolved" | "absolute_timeout") => {
    const finish = finishRef.current;
    if (finish.called()) return;
    try {
      const path = pathnameRef.current;
      const { status: st, session: ses } = authRef.current;
      const hasSession = st === "authed" && !!(ses as any)?.user;
      const hasTokens = hasStoredAuthTokens();
      const userId: string | null = (ses as any)?.user?.id ?? (hasTokens ? getStoredAuthUserId() : null);
      const workflow = hasSession || hasTokens ? getRecoverableWorkflow(userId) : null;
      const bypass = shouldBypassRouting(path);
      const crashLoop =
        !bypass && (hasSession || hasTokens) && path.startsWith("/resume-job/")
          ? checkResumeJobCrashLoop(path, !!workflow)
          : false;

      const decision = decideStartupRoute({
        hasSession,
        hasStoredTokens: hasTokens,
        pathname: path,
        workflow,
        bypass,
        preserveRoute: shouldPreserveRoute(path),
        isAppraisalRoute: isAppraisalRoute(path),
        crashLoop,
      });

      if (decision.reason === "crash_loop_dashboard") {
        try { localStorage.setItem("tm_crash_loop_redirect", "1"); } catch {}
      }

      const bootPath = bootPathRef.current;
      emitStartupEvent(
        bootPath === "/" || bootPath === "" ? "startup.cold_start" : "startup.webview_reload",
        source,
        bootPath,
      );
      if (hasSession) {
        emitStartupEvent("startup.session_restored", source, path);
        if (isBiometricOptedIn()) emitStartupEvent("startup.face_id_skipped_valid_session", decision.reason, path);
      } else if (decision.target === "/auth" && isBiometricOptedIn()) {
        emitStartupEvent("startup.face_id_required", decision.reason, path);
      }
      if (decision.reason === "active_workflow_restored") {
        emitStartupEvent("startup.active_workflow_restored", source, decision.target ?? path);
      }

      logger.debug("[NativeStartup] decision:", decision.reason, "source:", source);
      finish(decision.target);
    } catch (e) {
      logger.error("[NativeStartup] Startup decision error:", e);
      emitStartupEvent("startup.recovery_failed", "decision_error", pathnameRef.current);
      finish(hasStoredAuthTokens() ? "/dashboard" : "/auth");
    }
  };

  // Safety net: auth restoration never resolved.
  useEffect(() => {
    if (!isNative) return;
    const t = setTimeout(() => {
      if (finishRef.current.called()) return;
      console.warn("[NativeStartupOverlay] ABSOLUTE timeout reached — deciding with current state");
      emitStartupEvent("startup.recovery_failed", "absolute_timeout", pathnameRef.current);
      decide("absolute_timeout");
    }, ABSOLUTE_MAX_SPLASH_MS);
    return () => clearTimeout(t);
  }, []);

  // Decide as soon as AuthContext has finished restoring the persisted session.
  useEffect(() => {
    if (!isNative) return;
    if (status === "loading") {
      logger.debug("[NativeStartup] Waiting for auth…");
      return;
    }
    decide("auth_resolved");
  }, [status, session]);

  if (!visible) return null;
  return <Splash fadingOut={fadingOut} />;
}

export default NativeStartupOverlay;
